import { useEffect, useRef, useState } from "react";
import { api, LOGO_URL } from "../lib/api";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import { Printer, FileImage, FileText as FilePdf, Code2, ChevronLeft, AlertTriangle, Snowflake } from "lucide-react";
import { toast } from "sonner";

/**
 * AMINOPLEX LABEL DESIGNER — Production-Ready
 * Single-side label (front only). Black-on-white. Gold ink reserved exclusively for
 * the AP+AMINOPLEX logo (separate Illustrator layer via spot-color export).
 *
 * Sizes (final / with 0.125" bleed on all sides):
 *   10mL  → final 2.5" × 1.25"   · print file 2.75" × 1.50"   · NAD+ / Glutathione / Amino H2O
 *   3mL   → final 1.75" × 0.75"  · print file 2.00" × 1.00"   · all peptides
 */

const FORMATS = {
    "10ml": { label: "10 mL Vial", final_in: { w: 2.5, h: 1.25 }, bleed_in: 0.125, screen_scale: 168, helper: "NAD+ · Glutathione · Amino H2O" },
    "3ml":  { label: "3 mL Vial",  final_in: { w: 1.75, h: 0.75 }, bleed_in: 0.125, screen_scale: 200, helper: "All research peptides" },
};

/**
 * Fit a font size so a string never exceeds maxWidth. Uses a conservative
 * char-width factor for heavy weight sans-serif. Caps at maxFontSize so
 * short product names (e.g. NAD+) fill the available space.
 */
function fitFontSize(text, maxWidth, maxFontSize, charWidthFactor = 0.6) {
    const len = Math.max((text || "").length, 1);
    const ideal = maxWidth / (len * charWidthFactor);
    return Math.min(maxFontSize, ideal);
}

export default function LabelDesigner() {
    const [products, setProducts] = useState([]);
    const [selected, setSelected] = useState(null);

    useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);

    if (!selected) return <ProductPicker products={products} onPick={setSelected} />;
    return <Editor product={selected} onBack={() => setSelected(null)} />;
}

function ProductPicker({ products, onPick }) {
    return (
        <div data-testid="label-designer" className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <header className="mb-12">
                    <div className="data-label text-brand mb-3">LABEL SYSTEM · ADMIN</div>
                    <h1 className="font-display text-5xl md:text-6xl leading-tight mb-4" style={{ fontWeight: 700 }}>
                        Print-Ready <span className="italic text-brand">Aminoplex</span> Labels
                    </h1>
                    <p className="text-secondary max-w-2xl">
                        Pick a product to generate its production-ready file for your manufacturer. Single-side, black on white,
                        with gold ink reserved for the AP + AMINOPLEX logo on a separate layer.
                    </p>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                    <SpecCard label="10 mL Format" v={`2.5" × 1.25"`}  sub={`Print: 2.75" × 1.50" · 0.125" bleed`} />
                    <SpecCard label="3 mL Format"  v={`1.75" × 0.75"`} sub={`Print: 2.00" × 1.00" · 0.125" bleed`} />
                    <SpecCard label="Export"        v="PDF · SVG · PNG" sub="300 DPI · Illustrator layers · Flat gold ink only" />
                </div>

                <div className="data-label text-brand mb-4">SELECT A PRODUCT</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {products.map((p) => (
                        <button key={p.id} onClick={() => onPick(p)} data-testid={`label-pick-${p.slug}`} className="card card-hover p-4 text-left group">
                            <div className="aspect-square bg-[var(--bg)] border border-[var(--border)] overflow-hidden mb-3 flex items-center justify-center">
                                <img src={vialImage(p)} alt={p.name} className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                            </div>
                            <div className="data-label mb-1">{p.vial_size?.includes("10") ? "10 mL" : "3 mL"}</div>
                            <div className="font-display text-lg text-primary">{p.name}</div>
                            <div className="font-mono text-xs text-brand mt-1">{p.strength}</div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function vialImage(p) {
    // Use the canonical image URL from the API (already mapped to the correct vial render in seed_data.py).
    return p.image;
}

function SpecCard({ label, v, sub }) {
    return (
        <div className="card p-5">
            <div className="data-label text-brand">{label}</div>
            <div className="font-display text-2xl text-primary mt-2">{v}</div>
            <div className="font-mono text-[0.65rem] text-muted mt-1">{sub}</div>
        </div>
    );
}

function slugAbbrev(slug) {
    return slug?.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6) || "AP";
}
function downloadDataURL(url, name) {
    const a = document.createElement("a"); a.href = url; a.download = name; a.click();
}
function downloadBlob(text, type, name) {
    const blob = new Blob([text], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 1500);
}
function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }
function escapeXml(s) {
    return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
}

function Editor({ product, onBack }) {
    const [format, setFormat] = useState(product.vial_size?.includes("10") ? "10ml" : "3ml");
    const [data, setData] = useState({
        product_name: product.name,
        strength: product.strength,
        lot: `APX-${slugAbbrev(product.slug)}-001`,
        expiry: `${new Date().getFullYear() + 2}-${String(new Date().getMonth() + 1).padStart(2, "0")}`,
        distributed: "Aminoplex",
    });
    const hiddenRef     = useRef(null);
    const goldLayerRef  = useRef(null);
    const blackLayerRef = useRef(null);

    const f = FORMATS[format];
    const scale = f.screen_scale;
    const bleedPx = f.bleed_in * scale;
    const innerW = f.final_in.w * scale;
    const innerH = f.final_in.h * scale;
    const totalW = innerW + bleedPx * 2;
    const totalH = innerH + bleedPx * 2;

    const fname = (suffix, ext) => `aminoplex_${slugAbbrev(product.slug)}_${data.lot || "LOT"}_${format}${suffix ? "_" + suffix : ""}.${ext}`;

    const exportPNG = async () => {
        const c = await html2canvas(hiddenRef.current, { backgroundColor: null, scale: 4 });
        downloadDataURL(c.toDataURL("image/png"), fname("", "png"));
        toast.success("PNG exported");
    };

    const exportPDF = async () => {
        const printW = f.final_in.w + f.bleed_in * 2;
        const printH = f.final_in.h + f.bleed_in * 2;
        const c = await html2canvas(hiddenRef.current, { backgroundColor: null, scale: 6 });
        const pdf = new jsPDF({ orientation: "landscape", unit: "in", format: [printW, printH] });
        pdf.addImage(c.toDataURL("image/png"), "PNG", 0, 0, printW, printH);
        pdf.setFontSize(5); pdf.setTextColor(150);
        pdf.text(`Aminoplex · ${data.product_name} · ${data.strength} · Lot ${data.lot} · 0.125" bleed`, 0.08, printH - 0.05);
        pdf.save(fname("", "pdf"));
        toast.success("PDF exported · 300 DPI with bleed");
    };

    const exportSVG = () => {
        const wIn = f.final_in.w + f.bleed_in * 2;
        const hIn = f.final_in.h + f.bleed_in * 2;
        const w = wIn * 72; const h = hIn * 72;
        const small = f.final_in.w <= 2;
        const safeW = w * 0.88;
        const productMax = small ? 22 : 52;
        const productFs = fitFontSize((data.product_name || "").toUpperCase(), safeW, productMax, 0.62);
        const strengthFs = small ? 9 : 22;
        const headerFs = small ? 7 : 9;
        const footerFs = small ? 4.6 : 6.4;
        const headerY_ = small ? 9 : 22;
        const footerY_ = h - (small ? 5 : 12);
        const strengthY_ = footerY_ - footerFs - (small ? 3 : 6);
        const productY_ = strengthY_ - strengthFs - (small ? 2 : 4);
        const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
<rect width="${w}" height="${h}" fill="#FFFFFF"/>
<text x="${w / 2}" y="${headerY_}" text-anchor="middle" font-family="'Anek Telugu', sans-serif" font-weight="600" font-size="${headerFs}" letter-spacing="3" fill="#000000">RESEARCH USE ONLY</text>
<text x="${w / 2}" y="${productY_.toFixed(2)}" text-anchor="middle" font-family="'Anek Telugu', sans-serif" font-weight="800" font-size="${productFs.toFixed(2)}" fill="#000000">${escapeXml((data.product_name || "").toUpperCase())}</text>
<text x="${w / 2}" y="${strengthY_.toFixed(2)}" text-anchor="middle" font-family="'Anek Telugu', sans-serif" font-weight="600" font-size="${strengthFs}" fill="#000000">${escapeXml((data.strength || "").toUpperCase())}</text>
<text x="${10}" y="${footerY_.toFixed(2)}" font-family="'JetBrains Mono', monospace" font-size="${footerFs}" fill="#000000">LOT ${escapeXml(data.lot)}</text>
<text x="${w - 10}" y="${footerY_.toFixed(2)}" text-anchor="end" font-family="'JetBrains Mono', monospace" font-size="${footerFs}" fill="#000000">DIST ${escapeXml((data.distributed || "").toUpperCase())}</text>
</svg>`;
        const blob = new Blob([svg], { type: "image/svg+xml" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = fname("", "svg");
        a.click();
        toast.success("SVG exported · vector");
    };

    /**
     * PRODUCTION EXPORT · True Illustrator-ready vector files (no raster).
     *
     *   • _GOLD-LAYER.svg  → vector paths of the AP+AMINOPLEX logo on white
     *   • _BLACK-LAYER.svg → vector text elements for all label data
     *
     * SVG opens directly in Illustrator with editable paths/text. The printer
     * assigns gold ink to the gold layer and black ink to the black layer.
     * 1 inch = 72 points throughout (industry-standard SVG unit).
     */
    const exportProductionFiles = async () => {
        // Print-file dimensions in points (1 in = 72 pt)
        const printW = (f.final_in.w + f.bleed_in * 2) * 72;
        const printH = (f.final_in.h + f.bleed_in * 2) * 72;
        const small = f.final_in.w <= 2;

        // --- 1) Fetch the pre-traced vector logo ---
        let logoSvgText = "";
        try {
            const res = await fetch("/aminoplex_logo_vector.svg");
            logoSvgText = await res.text();
        } catch (e) {
            toast.error("Could not load vector logo file");
            return;
        }
        // Pull the <g> (path data) out of the traced SVG
        const pathMatch = logoSvgText.match(/<g[\s\S]*?<\/g>/);
        const viewBoxMatch = logoSvgText.match(/viewBox="([^"]+)"/);
        const logoGroup = pathMatch ? pathMatch[0] : "";
        const logoVB = viewBoxMatch ? viewBoxMatch[1].split(/\s+/).map(Number) : [0, 0, 834, 438];
        const [, , logoVBW, logoVBH] = logoVB;

        // Logo target size — scale to roughly 40% of label width
        const logoTargetW = printW * (small ? 0.34 : 0.32);
        const logoScale = logoTargetW / logoVBW;
        const logoTargetH = logoVBH * logoScale;
        const logoX = (printW - logoTargetW) / 2;
        const logoY = printH * (small ? 0.16 : 0.18);

        // --- 2) Build the gold-layer SVG (vector logo only) ---
        const goldSvg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${printW.toFixed(2)}pt" height="${printH.toFixed(2)}pt" viewBox="0 0 ${printW.toFixed(2)} ${printH.toFixed(2)}">
  <title>Aminoplex · ${data.product_name} · Lot ${data.lot} · GOLD LAYER</title>
  <desc>Spot-color plate. Render in gold ink only. Print file ${(f.final_in.w + f.bleed_in*2).toFixed(2)}" x ${(f.final_in.h + f.bleed_in*2).toFixed(2)}" with 0.125" bleed.</desc>
  <g id="gold-layer" fill="#000000">
    <g transform="translate(${logoX.toFixed(2)} ${logoY.toFixed(2)}) scale(${logoScale.toFixed(4)} ${(-logoScale).toFixed(4)}) translate(0 ${(-logoVBH).toFixed(2)})">
      ${logoGroup.replace(/<g[^>]*>/, "<g>").replace(/fill="[^"]*"/g, 'fill="#000000"')}
    </g>
  </g>
</svg>`;

        // --- 3) Build the black-layer SVG (vector text only) ---
        const cx = printW / 2;
        // Dynamic font sizing — long product names (BACTERIOSTATIC WATER, GLUTATHIONE)
        // auto-shrink to fit label width. Short names (NAD+) fill the space at max.
        const safeWidth = printW * 0.88;                 // 6% horizontal padding each side
        const productMax = small ? 22 : 52;
        const productFontSize = fitFontSize((data.product_name || "").toUpperCase(), safeWidth, productMax, 0.62);
        const strengthFontSize = small ? 9 : 22;
        const headerFontSize = small ? 6 : 9;
        const footerFontSize = small ? 4.6 : 6.4;

        // Vertical zones — guarantee NO overlap between strength text and the
        // LOT / DIST footer. Footer is anchored to the bottom; strength is
        // placed strictly above it with a safe gap.
        const headerY = small ? 9 : 22;
        const footerY = printH - (small ? 5 : 12);
        const footerTop = footerY - footerFontSize;     // top edge of footer text
        const safeGap = small ? 3 : 6;
        const strengthY = footerTop - safeGap;          // strength baseline (text sits above strengthY)
        const productY = strengthY - strengthFontSize - (small ? 2 : 4);

        const blackSvg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${printW.toFixed(2)}pt" height="${printH.toFixed(2)}pt" viewBox="0 0 ${printW.toFixed(2)} ${printH.toFixed(2)}">
  <title>Aminoplex · ${data.product_name} · Lot ${data.lot} · BLACK LAYER</title>
  <desc>Spot-color plate. Render in black ink. Print file ${(f.final_in.w + f.bleed_in*2).toFixed(2)}" x ${(f.final_in.h + f.bleed_in*2).toFixed(2)}" with 0.125" bleed.</desc>
  <g id="black-layer" fill="#000000" font-family="Helvetica, Arial, sans-serif">
    <text x="${cx}" y="${headerY}" text-anchor="middle" font-size="${headerFontSize}" font-weight="600" letter-spacing="3">RESEARCH USE ONLY</text>
    <text x="${cx}" y="${productY}" text-anchor="middle" font-size="${productFontSize}" font-weight="800">${escapeXml((data.product_name || "").toUpperCase())}</text>
    <text x="${cx}" y="${strengthY}" text-anchor="middle" font-size="${strengthFontSize}" font-weight="600">${escapeXml((data.strength || "").toUpperCase())}</text>
    <text x="${10}" y="${footerY}" font-size="${footerFontSize}" font-family="Courier, monospace">LOT ${escapeXml(data.lot)}</text>
    <text x="${printW - 10}" y="${footerY}" text-anchor="end" font-size="${footerFontSize}" font-family="Courier, monospace">DIST ${escapeXml((data.distributed || "").toUpperCase())}</text>
  </g>
</svg>`;

        // Bundle both layers + a README into a single ZIP — one download = no
        // browser "multiple files" permission popup that was silently dropping
        // the gold-layer file.
        const zip = new JSZip();
        const baseName = `aminoplex_${(data.product_name || "").replace(/[^a-z0-9]/gi, "")}_${data.lot}_${format === "10ml" ? "10ml" : "3ml"}`;
        zip.file(`${baseName}_GOLD-LAYER.svg`, goldSvg);
        zip.file(`${baseName}_BLACK-LAYER.svg`, blackSvg);
        zip.file("README.txt",
`AMINOPLEX — Print files for ${data.product_name} (Lot ${data.lot})
Format: ${f.label} · Final ${f.final_in.w}" x ${f.final_in.h}" · Print ${(f.final_in.w + f.bleed_in*2).toFixed(2)}" x ${(f.final_in.h + f.bleed_in*2).toFixed(2)}" (0.125" bleed all sides)

FILES:
• ${baseName}_GOLD-LAYER.svg   →  Print in gold ink only (logo)
• ${baseName}_BLACK-LAYER.svg  →  Print in black ink only (text)

INSTRUCTIONS FOR THE PRINTER:
1. Open both SVGs in Illustrator.
2. Each file is a separate spot-color plate — render the gold file in gold ink and the black file in black ink.
3. Trim to the final size after printing (artwork extends to the bleed edge).
`);
        const blob = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${baseName}.zip`;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 1500);

        toast.success(`Print files downloaded · ${baseName}.zip · contains both gold + black layer SVGs`);
    };

    return (
        <div data-testid="label-designer" className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <button onClick={onBack} className="btn btn-ghost mb-6" data-testid="back-to-picker">
                    <ChevronLeft size={14} /> All Products
                </button>

                <header className="mb-10 flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="data-label text-brand mb-3">LABEL FOR</div>
                        <h1 className="font-display text-4xl md:text-5xl">{product.name} <span className="text-brand">·</span> <span className="text-brand">{product.strength}</span></h1>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                        <button onClick={exportProductionFiles} data-testid="export-production" className="btn btn-solid">
                            <FilePdf size={14} /> Vector Files for Printer (SVG · Gold + Black)
                        </button>
                        <button onClick={exportPDF} data-testid="export-pdf" className="btn btn-gold">
                            <FilePdf size={14} /> Preview PDF
                        </button>
                        <button onClick={exportPNG} data-testid="export-png" className="btn"><FileImage size={14} /> PNG</button>
                        <button onClick={exportSVG} data-testid="export-svg" className="btn"><Code2 size={14} /> SVG</button>
                        <button onClick={() => window.print()} className="btn"><Printer size={14} /> Print</button>
                    </div>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-4 space-y-5">
                        <div className="card p-5">
                            <div className="data-label text-brand mb-4">FORMAT</div>
                            <div className="grid grid-cols-2 gap-2">
                                {Object.entries(FORMATS).map(([k, v]) => (
                                    <button key={k} onClick={() => setFormat(k)} data-testid={`format-${k}`}
                                            className={`p-3 border text-left transition-all ${format === k ? "border-[var(--brand)] bg-[var(--brand-soft)]" : "border-[var(--border)] hover:border-[var(--border-light)]"}`}>
                                        <div className="font-mono text-xs">{v.label}</div>
                                        <div className="data-label mt-1">{v.final_in.w}" × {v.final_in.h}"</div>
                                    </button>
                                ))}
                            </div>
                            <p className="data-label text-[0.6rem] mt-3">{f.helper}</p>
                        </div>

                        <div className="card p-5 space-y-3">
                            <div className="data-label text-brand">LABEL DATA · BAKED INTO EXPORT</div>
                            <Field label="Product Name" v={data.product_name} on={(v) => setData({ ...data, product_name: v })} />
                            <Field label="Strength" v={data.strength} on={(v) => setData({ ...data, strength: v })} />
                            <Field label="⚠ Lot Number (locked into file)" v={data.lot} on={(v) => setData({ ...data, lot: v })} />
                            <Field label="Distributed By" v={data.distributed} on={(v) => setData({ ...data, distributed: v })} />
                        </div>

                        <div className="card p-5 border-[var(--brand)]">
                            <div className="data-label text-brand mb-2">FOR YOUR PRINTER</div>
                            <p className="font-ui text-sm text-secondary leading-relaxed">
                                <strong className="text-[var(--text-primary)]">"Vector Files for Printer"</strong> exports
                                <strong> two true-vector SVG files</strong> — gold-ink plate (logo as editable vector paths) and
                                black plate (text as editable vector). Both open directly in Illustrator with no rasterized pixels.
                                Single side · flat gold ink · no foil.
                            </p>
                        </div>

                        <div className="card p-5">
                            <div className="data-label text-brand mb-3">PRINT SPECIFICATIONS</div>
                            <div className="space-y-2 font-mono text-[0.65rem] text-secondary">
                                <SpecLine k="Final Size"   v={`${f.final_in.w}" × ${f.final_in.h}"`} />
                                <SpecLine k="Print File"    v={`${(f.final_in.w + f.bleed_in * 2).toFixed(2)}" × ${(f.final_in.h + f.bleed_in * 2).toFixed(2)}"`} />
                                <SpecLine k="Bleed"         v={`0.125" all sides`} />
                                <SpecLine k="Resolution"    v="300 DPI" />
                                <SpecLine k="Layers"        v="Gold + Black (separate)" />
                                <SpecLine k="Color"         v="Flat gold ink + black · NO foil" />
                                <SpecLine k="Finish"        v="Matte / soft-touch recommended" />
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-8">
                        <div className="card p-8 lg:p-12 sticky top-32" style={{ background: "#111" }}>
                            <div className="flex items-center justify-between mb-6">
                                <div className="data-label">PRINT PREVIEW · {(scale / 72).toFixed(2)}× SCREEN SCALE</div>
                                <div className="data-label">DASHED = TRIM · OUTER = BLEED</div>
                            </div>

                            <div className="flex justify-center items-center p-10 bg-[#1a1a1a] overflow-auto">
                                <div ref={hiddenRef} style={{ width: totalW, height: totalH, background: "#FFFFFF", position: "relative", boxShadow: "0 30px 80px rgba(0,0,0,0.7)" }}>
                                    <div style={{ position: "absolute", top: bleedPx, left: bleedPx, width: innerW, height: innerH, border: "1px dashed rgba(0,0,0,0.18)", pointerEvents: "none" }} />
                                    <div style={{ position: "absolute", top: bleedPx, left: bleedPx, width: innerW, height: innerH }}>
                                        <LabelArt data={data} format={f} mode="full" />
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 grid grid-cols-3 gap-4 font-mono text-[0.65rem] text-muted">
                                <span>FINAL · {f.final_in.w}" × {f.final_in.h}"</span>
                                <span className="text-center">PRINT · {(f.final_in.w + f.bleed_in * 2).toFixed(2)}" × {(f.final_in.h + f.bleed_in * 2).toFixed(2)}"</span>
                                <span className="text-right">BLEED · {f.bleed_in}"</span>
                            </div>
                        </div>

                        {/* HIDDEN production-file renders — off-screen */}
                        <div style={{ position: "absolute", left: "-99999px", top: 0, pointerEvents: "none" }} aria-hidden>
                            {/* Gold layer · ONLY the logo (rendered solid black on white) */}
                            <div ref={goldLayerRef} style={{ width: totalW, height: totalH, background: "#FFFFFF", position: "relative" }}>
                                <div style={{ position: "absolute", top: bleedPx, left: bleedPx, width: innerW, height: innerH }}>
                                    <LabelArt data={data} format={f} mode="gold" />
                                </div>
                            </div>
                            {/* Black layer · all text */}
                            <div ref={blackLayerRef} style={{ width: totalW, height: totalH, background: "#FFFFFF", position: "relative", marginTop: 40 }}>
                                <div style={{ position: "absolute", top: bleedPx, left: bleedPx, width: innerW, height: innerH }}>
                                    <LabelArt data={data} format={f} mode="black" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, v, on }) {
    return (
        <div>
            <label className="label">{label}</label>
            <input value={v ?? ""} onChange={(e) => on(e.target.value)} className="input" />
        </div>
    );
}
function SpecLine({ k, v }) {
    return (
        <div className="flex justify-between gap-3 border-b border-[var(--border)] pb-1 last:border-0">
            <span className="text-muted">{k}</span>
            <span className="text-primary text-right">{v}</span>
        </div>
    );
}

/**
 * LabelArt — single front-side composition.
 *   mode = "full"   → live preview · logo in gold + text in black
 *   mode = "gold"   → ONLY the logo, rendered solid black (for the gold-ink plate)
 *   mode = "black"  → ALL TEXT, no logo (for the black plate)
 */
function LabelArt({ data, format, mode }) {
    const small = format.final_in.w <= 2;
    const showLogo = mode === "full" || mode === "gold";
    const showText = mode === "full" || mode === "black";
    const textColor = "#000000";
    const logoFilter = mode === "gold" ? "brightness(0)" : "none"; // make logo solid black for plate

    // Dynamic font sizing — long product names auto-shrink to fit the label width
    // while short names (NAD+) fill the available space.
    const innerWidthPx = format.final_in.w * format.screen_scale;
    const horizPaddingPct = small ? 0.05 : 0.06;        // matches the CSS padding below
    const safeWidthPx = innerWidthPx * (1 - horizPaddingPct * 2);
    const productMax = small ? 18 : 50;
    const productFontPx = fitFontSize((data.product_name || "").toUpperCase(), safeWidthPx, productMax, 0.62);
    const strengthFontPx = small ? 11 : 22;

    return (
        <div style={{
            width: "100%", height: "100%", position: "relative",
            display: "flex", flexDirection: "column", alignItems: "center",
            padding: small ? "4% 5%" : "5% 6%", fontFamily: "'Anek Telugu', sans-serif",
        }}>
            {/* RESEARCH USE ONLY · top */}
            {showText && (
                <div style={{ fontSize: small ? 6 : 9, letterSpacing: "0.28em", fontWeight: 600, color: textColor }}>
                    RESEARCH USE ONLY
                </div>
            )}

            {/* AP + AMINOPLEX logo · gold ink (separate layer) */}
            {showLogo && (
                <img
                    src={LOGO_URL}
                    alt="Aminoplex"
                    crossOrigin="anonymous"
                    style={{
                        width: small ? 70 : 125,
                        height: "auto",
                        objectFit: "contain",
                        marginTop: small ? 2 : 6,
                        marginBottom: small ? 2 : 6,
                        filter: logoFilter,
                    }}
                />
            )}

            {/* Product name · big bold black */}
            {showText && (
                <div style={{
                    flex: 1, display: "flex", flexDirection: "column", justifyContent: "center",
                    alignItems: "center", textAlign: "center", marginTop: small ? 1 : 2,
                }}>
                    <div style={{
                        color: textColor, fontWeight: 800, fontSize: productFontPx,
                        letterSpacing: "-0.01em", lineHeight: 0.95,
                    }}>
                        {(data.product_name || "").toUpperCase()}
                    </div>
                    <div style={{
                        color: textColor, fontWeight: 600, fontSize: strengthFontPx,
                        letterSpacing: "0.05em", marginTop: small ? 1 : 6,
                    }}>
                        {(data.strength || "").toUpperCase()}
                    </div>
                </div>
            )}

            {/* Bottom row · LOT (left) · DIST (right) */}
            {showText && (
                <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "flex-end",
                    width: "100%", color: textColor,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: small ? 4.6 : 6.4, letterSpacing: "0.08em",
                    marginTop: small ? 1 : 4,
                }}>
                    <span>LOT {data.lot}</span>
                    <span>DIST {(data.distributed || "").toUpperCase()}</span>
                </div>
            )}
        </div>
    );
}
